import { useNavigate } from 'react-router-dom'
import { DataTableColumn } from '@/components/common/DataTable'
import { ColumnConfig } from '@/components/ui/ColumnVisibilityControl'
import { CompleteSalarySnapshot } from '@/types/salarySnapshot'
import { Badge } from '@/components/ui/badge'
import { formatSEK } from '@/lib/formatters'
import { cn } from '@/lib/utils'

/**
 * Column configuration for the Review step DataTable
 * Mirrors the salary report columns but works with CompleteSalarySnapshot
 */

// Clickable driver name cell
function DriverNameCell({ snapshot, isExcluded }: { snapshot: CompleteSalarySnapshot; isExcluded: boolean }) {
  const navigate = useNavigate()

  return (
    <div>
      <button
        className={cn(
          'hover:text-blue-600 hover:underline text-left font-medium',
          isExcluded && 'line-through opacity-50'
        )}
        onClick={(e) => {
          e.stopPropagation()
          navigate(`/driver/${snapshot.driverId}`)
        }}
      >
        {snapshot.driverInfo.name}
      </button>
      {isExcluded && (
        <Badge variant="secondary" className="ml-2 text-xs">Exkluderad</Badge>
      )}
    </div>
  )
}

// Default columns for review table
export const DEFAULT_REVIEW_COLUMNS: ColumnConfig[] = [
  {
    key: 'driverName',
    label: 'Förare',
    visible: true,
    required: true,
    align: 'left'
  },
  {
    key: 'personalCode',
    label: 'Personnummer',
    visible: true,
    align: 'left'
  },
  {
    key: 'email',
    label: 'E-post',
    visible: true,
    align: 'left'
  },
  {
    key: 'grossEarnings',
    label: 'Bruttointäkter',
    visible: false,
    align: 'right'
  },
  {
    key: 'netEarnings',
    label: 'Nettointäkter',
    visible: false,
    align: 'right'
  },
  {
    key: 'tips',
    label: 'Dricks',
    visible: false,
    align: 'right'
  },
  {
    key: 'finalGrossSalary',
    label: 'Bruttolön',
    visible: true,
    align: 'right'
  },
  {
    key: 'taxAmount',
    label: 'Skatt',
    visible: false,
    align: 'right'
  },
  {
    key: 'amountToPay',
    label: 'Att utbetala',
    visible: true,
    align: 'right'
  }
]

// Create column definitions for the review table
export function createReviewColumns(
  excludedDriverIds: Set<string>
): DataTableColumn<CompleteSalarySnapshot>[] {
  return [
    {
      key: 'driverInfo' as keyof CompleteSalarySnapshot,
      header: 'Förare',
      sortable: true,
      align: 'left',
      cell: (_value, snapshot) => (
        <DriverNameCell
          snapshot={snapshot}
          isExcluded={excludedDriverIds.has(snapshot.driverId)}
        />
      )
    },
    {
      key: 'driverInfo' as keyof CompleteSalarySnapshot,
      header: 'Personnummer',
      sortable: false,
      align: 'left',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {snapshot.driverInfo.personalCode || '-'}
          </span>
        )
      }
    },
    {
      key: 'driverInfo' as keyof CompleteSalarySnapshot,
      header: 'E-post',
      sortable: false,
      align: 'left',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {snapshot.driverInfo.email || '-'}
          </span>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Bruttointäkter',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {formatSEK(snapshot.totals.grossEarnings)}
          </span>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Nettointäkter',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {formatSEK(snapshot.totals.netEarnings)}
          </span>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Dricks',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {formatSEK(snapshot.totals.tips)}
          </span>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Bruttolön',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn(isExcluded && 'opacity-50')}>
            {formatSEK(snapshot.totals.finalGrossSalary)}
          </span>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Skatt',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <div className={cn('text-right', isExcluded && 'opacity-50')}>
            <div>{formatSEK(snapshot.totals.taxAmount)}</div>
            <div className="text-xs text-muted-foreground">
              {snapshot.totals.effectiveTaxRate.toFixed(1)}%
            </div>
          </div>
        )
      }
    },
    {
      key: 'totals' as keyof CompleteSalarySnapshot,
      header: 'Att utbetala',
      sortable: true,
      align: 'right',
      cell: (_value, snapshot) => {
        const isExcluded = excludedDriverIds.has(snapshot.driverId)
        return (
          <span className={cn('font-medium text-green-700', isExcluded && 'opacity-50')}>
            {formatSEK(snapshot.totals.amountToPay)}
          </span>
        )
      }
    }
  ]
}

// Map column config keys to actual columns
const COLUMN_KEY_MAP: Record<string, number> = {
  driverName: 0,
  personalCode: 1,
  email: 2,
  grossEarnings: 3,
  netEarnings: 4,
  tips: 5,
  finalGrossSalary: 6,
  taxAmount: 7,
  amountToPay: 8
}

// Get visible columns based on configuration
export function getVisibleReviewColumns(
  visibleColumnKeys: string[],
  excludedDriverIds: Set<string>
): DataTableColumn<CompleteSalarySnapshot>[] {
  const allColumns = createReviewColumns(excludedDriverIds)
  return visibleColumnKeys
    .map(key => {
      const index = COLUMN_KEY_MAP[key]
      return index !== undefined ? allColumns[index] : null
    })
    .filter((col): col is DataTableColumn<CompleteSalarySnapshot> => col !== null)
}
